"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.supersedeNormSchema = exports.createNormSchema = exports.updateUnitSchema = exports.createUnitSchema = exports.updateNeutralActivitySchema = exports.createNeutralActivitySchema = exports.updateOperationSchema = exports.createOperationSchema = exports.updateProductSchema = exports.createProductSchema = exports.updateWorkerSchema = exports.createWorkerSchema = exports.updateWorkLineSchema = exports.createWorkLineSchema = exports.updateDailyEntryStatusSchema = exports.createDailyEntrySchema = exports.loginSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("./enums");
/** Decimal string: digits, optional single dot, optional leading minus. */
const decimalStringSchema = zod_1.z
    .string()
    .trim()
    .regex(/^-?\d+(\.\d+)?$/, "Vrednost mora biti broj.");
const positiveDecimalStringSchema = decimalStringSchema.refine((value) => Number(value) > 0, { message: "Trajanje mora biti veće od 0 minuta." });
/**
 * Fixed-width numeric business-code contract (Worker.employeeCode /
 * Product.code / Operation.code —
 * NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md).
 * Supersedes the earlier free-text `businessCodeField` contract
 * (NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md) —
 * per customer feedback, codes are now digits-only and canonicalized to a
 * fixed width per entity (Worker/Operation: 3, Product: 5), never letters/
 * signs/decimals/hyphens/underscores.
 *
 * Validated as a plain digit-count regex (`^\d{1,width}$`) — this is
 * DELIBERATELY a character-count check, not a numeric-magnitude check, so
 * it rejects "0012" for a width-3 code (4 digit CHARACTERS, even though its
 * numeric value 12 would fit) exactly as specified. Canonicalization is
 * pure string left-padding (`padStart`), never `Number()` — this preserves
 * exact digit sequences with no float/integer precision or leading-zero
 * loss concern, and keeps the stored/API representation a plain string
 * (never a numeric DB type).
 *
 * `undefined` (field omitted) is left as `undefined` so the service layer's
 * partial-patch pattern (`input.code !== undefined ? {...} : {}`) still
 * correctly leaves an omitted field untouched on update. An explicit `null`
 * or an empty/whitespace-only string both normalize to `null` — a code is
 * only meaningful non-null/non-empty (the tenant-scoped uniqueness's own
 * trigger), so two independently cleared codes must never collide as if
 * they were both the literal value `""`.
 */
function fixedWidthNumericCodeField(width, invalidMessage) {
    return zod_1.z
        .string()
        .trim()
        .nullable()
        .optional()
        .refine((value) => value === undefined || value === null || value === "" || new RegExp(`^\\d{1,${width}}$`).test(value), { message: invalidMessage })
        .transform((value) => value === undefined ? undefined : value === null || value === "" ? null : value.padStart(width, "0"));
}
exports.loginSchema = zod_1.z.object({
    // Tenant login identifier (company.code — DATABASE_SCHEMA_AND_MIGRATION_PLAN.md
    // §2.1 correction, migration 20260810140000_add_company_login_code).
    // Required so the backend can identify exactly one AppUser (company code +
    // email) BEFORE password verification — email alone is only unique WITHIN
    // a company, never across companies.
    companyCode: zod_1.z.string().trim().min(1, "Unesite šifru firme."),
    email: zod_1.z.string().trim().min(1, "Unesite email.").email("Unesite ispravan email."),
    password: zod_1.z.string().min(1, "Unesite lozinku."),
});
exports.createDailyEntrySchema = zod_1.z.object({
    workerId: zod_1.z.string().uuid("Izaberite radnika iz liste."),
    workDate: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Neispravan format datuma."),
});
/**
 * "Ponovo otvori" (2026-09-09 brainstorm) added CONFIRMED -> DRAFT as a
 * legitimate transition, not just DRAFT -> CONFIRMED — the single
 * "Potvrdi unos" / "Ponovo otvori" toggle on the Daily Entry page cycles
 * between the two. `locked` is deliberately NOT accepted here — it is a
 * reserved status with no active transition anywhere in the app yet.
 */
exports.updateDailyEntryStatusSchema = zod_1.z.object({
    status: zod_1.z.union([zod_1.z.literal(enums_1.EntryStatus.CONFIRMED), zod_1.z.literal(enums_1.EntryStatus.DRAFT)]),
});
const baseWorkLineFields = {
    performedAtDepartmentId: zod_1.z.string().uuid().nullish(),
    workOrderReference: zod_1.z.string().trim().max(120).nullish(),
    note: zod_1.z.string().trim().max(500).nullish(),
    durationMinutes: positiveDecimalStringSchema,
};
const normedWorkLineSchema = zod_1.z.object({
    entryType: zod_1.z.literal(enums_1.EntryType.NORMED_WORK),
    productId: zod_1.z.string().uuid().nullish(),
    operationId: zod_1.z.string().uuid("Izaberite operaciju iz liste."),
    neutralActivityId: zod_1.z.null().optional(),
    reportedQuantity: decimalStringSchema.refine((v) => Number(v) >= 0, {
        message: "Ostvarena količina mora biti 0 ili veća.",
    }),
    unitId: zod_1.z.string().uuid().nullish(),
    ...baseWorkLineFields,
});
const neutralActivityLineSchema = zod_1.z.object({
    entryType: zod_1.z.literal(enums_1.EntryType.NEUTRAL_ACTIVITY),
    neutralActivityId: zod_1.z.string().uuid("Izaberite vrstu neutralne aktivnosti."),
    productId: zod_1.z.null().optional(),
    operationId: zod_1.z.null().optional(),
    reportedQuantity: zod_1.z.null().optional(),
    unitId: zod_1.z.null().optional(),
    ...baseWorkLineFields,
});
const unnormedWorkLineSchema = zod_1.z.object({
    entryType: zod_1.z.literal(enums_1.EntryType.UNNORMED_WORK),
    operationId: zod_1.z.string().uuid("Izaberite operaciju iz liste."),
    productId: zod_1.z.string().uuid().nullish(),
    neutralActivityId: zod_1.z.null().optional(),
    reportedQuantity: decimalStringSchema.nullish(),
    unitId: zod_1.z.string().uuid().nullish(),
    ...baseWorkLineFields,
});
/**
 * discriminatedUnion requires plain ZodObject members (not ZodEffects), so the
 * UNNORMED_WORK quantity/unit pair-rule is applied as a .refine() on the union
 * itself rather than on unnormedWorkLineSchema directly.
 */
exports.createWorkLineSchema = zod_1.z
    .discriminatedUnion("entryType", [
    normedWorkLineSchema,
    neutralActivityLineSchema,
    unnormedWorkLineSchema,
])
    .refine((value) => value.entryType !== enums_1.EntryType.UNNORMED_WORK ||
    (value.reportedQuantity == null && value.unitId == null) ||
    (value.reportedQuantity != null && value.unitId != null), {
    message: "Unesite i količinu i jedinicu, ili ostavite oba polja prazna.",
    path: ["unitId"],
});
/**
 * Partial update payload — cross-field rules (entryType-specific requiredness,
 * UNNORMED_WORK quantity/unit pair rule) are re-validated server-side against
 * the merged (existing + patch) row, not re-derived here from a partial shape.
 */
exports.updateWorkLineSchema = zod_1.z.object({
    entryType: zod_1.z.nativeEnum(enums_1.EntryType).optional(),
    productId: zod_1.z.string().uuid().nullish(),
    operationId: zod_1.z.string().uuid().nullish(),
    neutralActivityId: zod_1.z.string().uuid().nullish(),
    performedAtDepartmentId: zod_1.z.string().uuid().nullish(),
    reportedQuantity: decimalStringSchema.nullish(),
    unitId: zod_1.z.string().uuid().nullish(),
    workOrderReference: zod_1.z.string().trim().max(120).nullish(),
    note: zod_1.z.string().trim().max(500).nullish(),
    durationMinutes: positiveDecimalStringSchema.optional(),
    reason: zod_1.z.string().trim().min(1).optional(),
});
// ---------------------------------------------------------------------------
// Operational MVP Slice A — Workers / Products / Operations CRUD
// (NORMAX_PERFORMANCE_OPERATIONAL_MVP_SLICE_A_IMPLEMENTATION_REPORT.md).
// No new columns anywhere here — every field below already exists on the
// corresponding Prisma model; this is validation only, not a schema change.
// ---------------------------------------------------------------------------
exports.createWorkerSchema = zod_1.z.object({
    firstName: zod_1.z.string().trim().min(1, "Unesite ime.").max(100, "Ime je predugačko."),
    lastName: zod_1.z.string().trim().min(1, "Unesite prezime.").max(100, "Prezime je predugačko."),
    // Nullish, not required — Worker.employee_code is nullable in schema.
    // Tenant-scoped uniqueness (companyId + employeeCode, non-null values
    // only) is now enforced at the DB layer — see
    // NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md.
    employeeCode: fixedWidthNumericCodeField(3, "Šifra mora sadržati od 1 do 3 cifre."),
});
/** Also the activate/deactivate payload — `{ active: false }` / `{ active: true }`. */
exports.updateWorkerSchema = zod_1.z.object({
    firstName: zod_1.z.string().trim().min(1, "Unesite ime.").max(100, "Ime je predugačko.").optional(),
    lastName: zod_1.z.string().trim().min(1, "Unesite prezime.").max(100, "Prezime je predugačko.").optional(),
    employeeCode: fixedWidthNumericCodeField(3, "Šifra mora sadržati od 1 do 3 cifre."),
    active: zod_1.z.boolean().optional(),
});
exports.createProductSchema = zod_1.z.object({
    canonicalName: zod_1.z.string().trim().min(1, "Unesite naziv proizvoda.").max(200, "Naziv je predugačak."),
    // Optional business code, tenant-scoped-unique when non-null/non-empty —
    // see NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md.
    code: fixedWidthNumericCodeField(5, "Šifra mora sadržati od 1 do 5 cifara."),
});
exports.updateProductSchema = zod_1.z.object({
    canonicalName: zod_1.z
        .string()
        .trim()
        .min(1, "Unesite naziv proizvoda.")
        .max(200, "Naziv je predugačak.")
        .optional(),
    code: fixedWidthNumericCodeField(5, "Šifra mora sadržati od 1 do 5 cifara."),
    active: zod_1.z.boolean().optional(),
});
exports.createOperationSchema = zod_1.z.object({
    canonicalName: zod_1.z.string().trim().min(1, "Unesite naziv operacije.").max(200, "Naziv je predugačak."),
    code: fixedWidthNumericCodeField(3, "Šifra mora sadržati od 1 do 3 cifre."),
});
exports.updateOperationSchema = zod_1.z.object({
    canonicalName: zod_1.z
        .string()
        .trim()
        .min(1, "Unesite naziv operacije.")
        .max(200, "Naziv je predugačak.")
        .optional(),
    code: fixedWidthNumericCodeField(3, "Šifra mora sadržati od 1 do 3 cifre."),
    active: zod_1.z.boolean().optional(),
});
/**
 * NeutralActivity ("Neutralna aktivnost") — Neutral/Unnormed Operational
 * Completion. `category` is `String?` in `prisma/schema.prisma` — a plain
 * nullable free-text column, NOT a Postgres/Prisma enum. No controlled
 * taxonomy exists anywhere in code; DATABASE_SCHEMA_AND_MIGRATION_PLAN.md/
 * PROPOSED_DATA_MODEL.md only give illustrative examples ("npr. odsustvo/
 * pauza/obuka/zastoj/ostalo"), never an enforced list. Inventing an enum here
 * would be stricter than the schema actually requires — `category` stays a
 * trimmed, optional free-text field, exactly matching the DB column's real
 * contract (see NORMAX_PERFORMANCE_NEUTRAL_UNNORMED_IMPLEMENTATION_REPORT.md
 * §4 for the full decision record).
 */
exports.createNeutralActivitySchema = zod_1.z.object({
    canonicalName: zod_1.z
        .string()
        .trim()
        .min(1, "Unesite naziv neutralne aktivnosti.")
        .max(200, "Naziv je predugačak."),
    category: zod_1.z.string().trim().max(100, "Kategorija je predugačka.").nullish(),
});
exports.updateNeutralActivitySchema = zod_1.z.object({
    canonicalName: zod_1.z
        .string()
        .trim()
        .min(1, "Unesite naziv neutralne aktivnosti.")
        .max(200, "Naziv je predugačak.")
        .optional(),
    category: zod_1.z.string().trim().max(100, "Kategorija je predugačka.").nullish(),
    active: zod_1.z.boolean().optional(),
});
/**
 * Unit ("Jedinica mere") — Operational MVP Slice B follow-up (Final
 * Operability Audit): the pre-existing `Unit` Prisma model has no `active`
 * column (unlike Product/Operation), so there is no activate/deactivate
 * payload here — this is a schema fact, not an oversight (adding one would
 * be a migration, out of scope for this audit-driven fix).
 */
exports.createUnitSchema = zod_1.z.object({
    code: zod_1.z.string().trim().min(1, "Unesite oznaku jedinice mere.").max(20, "Oznaka je predugačka."),
    name: zod_1.z.string().trim().min(1, "Unesite naziv jedinice mere.").max(100, "Naziv je predugačak."),
});
exports.updateUnitSchema = zod_1.z.object({
    code: zod_1.z.string().trim().min(1, "Unesite oznaku jedinice mere.").max(20, "Oznaka je predugačka.").optional(),
    name: zod_1.z.string().trim().min(1, "Unesite naziv jedinice mere.").max(100, "Naziv je predugačak.").optional(),
});
// ---------------------------------------------------------------------------
// Operational MVP Slice B — Norm management (NORMAX_PERFORMANCE_OPERATIONAL_MVP_SLICE_B_NORMS_IMPLEMENTATION_REPORT.md).
// No new columns — every field below already exists on the Prisma Norm model;
// this is validation only, not a schema change.
// ---------------------------------------------------------------------------
const normDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Neispravan format datuma.");
const normStandardQuantitySchema = decimalStringSchema.refine((value) => Number(value) > 0, {
    message: "Normirana količina mora biti veća od 0.",
});
/**
 * First version of a norm for a (product, operation) combination — see
 * norms.logic.ts for the overlap/versioning rules enforced server-side
 * beyond field-level shape.
 *
 * `productId` is REQUIRED — Slice B Final Business Contract decision: for
 * the operational Performance Intelligence MVP, a Norm is always tied to
 * Product + Operation + Unit; the standard quantity is configured for that
 * exact Product+Operation combination. The Vertical-Slice-era product-less
 * path (`productLessConfirmed`) was flexibility that predates this decision
 * and is not part of the operational MVP — removed from this contract. The
 * underlying `Norm.productId` DB column stays nullable (pre-existing rows,
 * legacy compatibility — no migration); this API contract is deliberately
 * stricter than the schema permits, not a reflection of what the schema
 * itself requires.
 *
 * `baseDurationMinutes` is deliberately NOT a client-supplied field —
 * Production Usability Fix decision: the base duration is always resolved
 * server-side from the tenant's current Company Performance Settings
 * (`standardAvailableMinutes`) at the moment of create/supersede, never
 * trusted from the client. See norms.service.ts.
 */
exports.createNormSchema = zod_1.z.object({
    productId: zod_1.z.string().uuid("Izaberite proizvod iz liste."),
    operationId: zod_1.z.string().uuid("Izaberite operaciju iz liste."),
    standardQuantity: normStandardQuantitySchema,
    unitId: zod_1.z.string().uuid("Izaberite jedinicu mere."),
    effectiveFrom: normDateSchema,
});
/**
 * Creates a successor version of an existing norm — product/operation are
 * NOT re-selectable here (a supersede is a new version of the SAME
 * product+operation lineage, never a different combination; start a fresh
 * `POST /norms` for that instead). `baseDurationMinutes` is server-resolved
 * here too — see createNormSchema's comment above.
 */
/**
 * `applyRetroactively` (2026-09-09 brainstorm) — opt-in, unchecked by
 * default. When true, EVERY WorkLine referencing the superseded norm gets
 * recomputed under the new standardQuantity/baseDurationMinutes, regardless
 * of its parent day's status (draft or confirmed alike — explicit user
 * decision, see the design doc §4.2). `reason` is required ONLY in that
 * case — matches the Daily Entry deletion precedent ("never silent" only
 * where real risk exists, not everywhere).
 */
exports.supersedeNormSchema = zod_1.z
    .object({
    standardQuantity: normStandardQuantitySchema,
    unitId: zod_1.z.string().uuid("Izaberite jedinicu mere."),
    effectiveFrom: normDateSchema,
    applyRetroactively: zod_1.z.boolean().default(false),
    reason: zod_1.z.string().trim().optional(),
})
    .refine((data) => !data.applyRetroactively || !!data.reason?.trim(), {
    message: "Razlog je obavezan kada se izmena primenjuje i na ranije unose.",
    path: ["reason"],
});
//# sourceMappingURL=schemas.js.map